/**
 * BottomNav
 *
 * Phone-only primary navigation. Replaces the sidebar drawer on small screens
 * with a thumb-reachable bottom bar (4 destinations + a "More" sheet), which is
 * the native Android/Material pattern and avoids the hidden-nav discoverability
 * problem of a hamburger drawer.
 *
 * Rendered only when `isMobile` is true – see SystemLayout.
 */

import { useCallback, useMemo, useState } from 'react';
import { Drawer } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  IconGrid,
  IconInfo,
  IconCheckSquare,
  IconWallet,
  IconDocument,
  IconQuestionBank,
  IconTrophy,
} from '../components/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { getMobileNav, resolveActivePath } from './MobileNavConfig';
import { hapticSelect } from '../../core/native';
import { APP_INFO } from '../../core/constants';
import { IconTile } from '../components/primitives';
import './layout.css';

/**
 * The concept → tile registry (app-shell.md §5). Colour lands only where
 * `design-language.md` §2 assigns a meaning; everything else is a neutral tile
 * with the destination's own glyph, so four coloured tiles among twelve read as
 * deliberate rather than as a sticker sheet.
 */
const tileFor = (path) => {
  if (path.endsWith('/attendance')) return { tone: 'pistachio', icon: <IconCheckSquare /> };
  if (path.endsWith('/tuition') || path.endsWith('/balance')) {
    return { tone: 'terracotta', icon: <IconWallet /> };
  }
  if (path.includes('/mock-exams') || path.includes('/practice-tests')) {
    return { tone: 'plum', icon: <IconDocument /> };
  }
  if (path.includes('/question-bank')) {
    return { tone: 'indigo', icon: <IconQuestionBank /> };
  }
  if (path.endsWith('/ranking')) return { tone: 'saffron', icon: <IconTrophy /> };
  return null;
};

const BottomNav = ({ user, onOpenChangelog }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  const { primary, more } = useMemo(
    () => getMobileNav(user?.roleName || '', t),
    [user?.roleName, t],
  );

  const activePath = useMemo(
    () => resolveActivePath(primary, location.pathname),
    [primary, location.pathname],
  );

  const moreActivePath = useMemo(
    () => resolveActivePath(more, location.pathname),
    [more, location.pathname],
  );

  // A route that lives in the More sheet highlights the More tab, rather than
  // leaving the bar with nothing selected.
  const activeInMore = !activePath && Boolean(moreActivePath);

  const onTabPress = useCallback(() => {
    hapticSelect();
  }, []);

  const onMorePress = useCallback(() => {
    hapticSelect();
    setMoreOpen(true);
  }, []);

  const onMoreItemPress = useCallback(
    (path) => {
      hapticSelect();
      setMoreOpen(false);
      navigate(path);
    },
    [navigate],
  );

  return (
    <>
      <nav className="lms-bottom-nav" aria-label={t('common.primaryNavigation')}>
        {primary.map((item) => {
          const isActive = activePath === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onTabPress}
              className={`lms-bottom-nav__item ${isActive ? 'lms-bottom-nav__item--active' : ''}`}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className="lms-bottom-nav__icon">{item.icon}</span>
              <span className="lms-bottom-nav__label">{item.label}</span>
            </Link>
          );
        })}

        <button
          type="button"
          onClick={onMorePress}
          className={`lms-bottom-nav__item ${activeInMore ? 'lms-bottom-nav__item--active' : ''}`}
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
        >
          <span className="lms-bottom-nav__icon">
            <IconGrid />
          </span>
          <span className="lms-bottom-nav__label">{t('common.more')}</span>
        </button>
      </nav>

      <Drawer
        placement="bottom"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        height="auto"
        title={t('common.more')}
        className="lms-more-sheet"
        styles={{ body: { padding: 'var(--space-3)' } }}
      >
        <div className="lms-more-sheet__grid">
          {more.map((item) => {
            const isActive = moreActivePath === item.path;
            const tile = tileFor(item.path) || { tone: 'neutral', icon: item.icon };
            return (
              <button
                type="button"
                key={item.path}
                onClick={() => onMoreItemPress(item.path)}
                className={`lms-more-sheet__item ${isActive ? 'lms-more-sheet__item--active' : ''}`}
                aria-current={isActive ? 'page' : undefined}
              >
                <IconTile
                  icon={tile.icon}
                  tone={isActive ? 'primary' : tile.tone}
                  variant={isActive ? 'solid' : 'tint'}
                  size="md"
                />
                <span className="lms-more-sheet__label">{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* The version/changelog trigger lives in the sidebar on desktop; the
            More sheet is its home on mobile so the entry point isn't lost. */}
        <button
          type="button"
          className="lms-more-sheet__version"
          onClick={() => {
            setMoreOpen(false);
            onOpenChangelog?.();
          }}
        >
          <IconInfo />
          <span>
            {t('common.platformVersion')} <span className="lms-figure">v{APP_INFO.VERSION}</span>
          </span>
        </button>
      </Drawer>
    </>
  );
};

BottomNav.propTypes = {
  user: PropTypes.object,
  onOpenChangelog: PropTypes.func,
};

export default BottomNav;
